import { useMemo } from 'react';
import { useCustomerAuth } from '@backpackjs/storefront';

import { Section, Link } from '@snippets';
import { calcResponsiveHeight, withInView } from '@utils';
import { useParallax } from '@hooks/useParallax';
import { Box, Container } from 'theme-ui';

import { Image } from './Image';
import { Content, ContentBelow } from './Content';
import { themed } from './Hero.theme';
import { Schema } from './Hero.schema';

export const Hero = withInView(
  themed(({ theme, cms }) => {
    const { button, image, text, contentSettings, section, parallaxSettings } =
      cms;
    const { placement, placementDt } = { ...contentSettings };
    const { customerInit, customer } = useCustomerAuth();
    const {
      enableByTags = '',
      disableByTags = '',
      enableDisableBasedOnTags = false,
    } = section || {};
    const enableByTagsArray = useMemo(() => {
      if (!enableByTags || enableByTags?.trim() === '') return [];
      return enableByTags?.split(',')?.map((tag) => tag.trim());
    }, [enableByTags]);

    const disableByTagsArray = useMemo(() => {
      if (!disableByTags || disableByTags?.trim() === '') return [];
      return disableByTags?.split(',')?.map((tag) => tag.trim());
    }, [disableByTags]);

    const customerTags = useMemo(() => {
      return customer?.tags || [];
    }, [customer?.tags]);

    const enableSection = useMemo(() => {
      if (!customerInit) return false;
      if (
        disableByTagsArray?.length > 0 &&
        disableByTagsArray?.find((tag) => customerTags.includes(tag))
      ) {
        return false;
      }
      if (
        enableByTagsArray?.length > 0 &&
        enableByTagsArray?.find((tag) => customerTags.includes(tag)) ===
          undefined
      ) {
        return false;
      }
      return true;
    }, [enableByTagsArray, disableByTagsArray, customerInit, customerTags]);

    const parallax = useParallax({
      enabled: parallaxSettings?.enabled,
      textColorOutside: parallaxSettings?.textColorOutside,
      textColorInside: parallaxSettings?.textColorInside,
    });

    const content = useMemo(
      () => ({
        image,
        text,
        button,
        settings: contentSettings,
        parallax,
      }),
      [cms, parallax]
    );

    const cta = button?.buttons?.[0]?.cta;
    const clickable = button?.clickableHero && !!cta?.url;

    const height = useMemo(() => {
      return [
        calcResponsiveHeight({
          maxHeight: section?.mobile?.maxHeight,
          viewport: 768,
        }),
        calcResponsiveHeight({
          maxHeight: section?.desktop?.maxHeight,
          viewport: 1280,
        }),
      ];
    }, [section?.mobile?.maxHeight, section?.desktop?.maxHeight]);

    if (enableDisableBasedOnTags && !enableSection) return null;

    return (
      <Section section={section} suppressHydrationWarning>
        <Link
          data-comp={Hero.displayName}
          href={clickable ? cta?.url : null}
          newTab={cta?.newTab}
          aria-label={clickable && button?.hideButtons ? cta?.text : ''}
        >
          <Container
            ref={parallax.heroRef}
            sx={{
              ...theme.hero,
              height,
              minHeight: [
                `${section?.mobile?.minHeight}px`,
                `${section?.desktop?.minHeight}px`,
              ],
              maxHeight: [
                `${section?.mobile?.maxHeight}px`,
                `${section?.desktop?.maxHeight}px`,
              ],
              cursor: clickable ? 'pointer' : 'auto',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Image
              image={image}
              preload={section.aboveTheFold}
              aboveTheFold={section.aboveTheFold}
              parallax={parallax}
            />

            <Box
              sx={{
                display: [
                  placement === 'relative' ? 'none' : 'block',
                  placementDt === 'relative' ? 'none' : 'block',
                ],
                height: '100%',
              }}
            >
              <Content content={content} clickable={clickable} />
            </Box>
          </Container>
        </Link>
        {/* Show if content is placed below the hero image. */}

        <Box
          sx={{
            display: [
              placement === 'relative' ? 'block' : 'none',
              placementDt === 'relative' ? 'block' : 'none',
            ],
          }}
        >
          <ContentBelow bgColor={section?.bgColor} content={content} />
        </Box>
      </Section>
    );
  }),
  { triggerOnce: true }
);

Hero.displayName = 'Hero';
Hero.Schema = Schema;
